import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Search, Send, MessageSquare, Bot, User, Sparkles, Headset, ExternalLink } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { CONVO_CHANNELS, convoStatusMeta, convoStateMeta, fmtDateTime } from "@/config/crm";

export default function Conversations() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [convos, setConvos] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [ctx, setCtx] = useState(null);
  const [search, setSearch] = useState("");
  const [text, setText] = useState("");
  const [aiMode, setAiMode] = useState(true);
  const [sending, setSending] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [channel, setChannel] = useState("WhatsApp");
  const [contactName, setContactName] = useState("");
  const [leads, setLeads] = useState([]);
  const [leadId, setLeadId] = useState("");
  const scrollRef = useRef(null);

  useEffect(() => { api.get("/leads").then(({ data }) => setLeads(data.leads)).catch(() => {}); }, []);

  const loadList = useCallback(async () => {
    const params = search.trim() ? { search: search.trim() } : {};
    const { data } = await api.get("/conversations", { params });
    setConvos(data.conversations);
    return data.conversations;
  }, [search]);

  const openConvo = useCallback(async (id) => {
    const [{ data: thread }, { data: context }] = await Promise.all([
      api.get(`/conversations/${id}`), api.get(`/conversations/${id}/context`),
    ]);
    setActive(thread.conversation);
    setMessages(thread.messages);
    setCtx(context);
    setTimeout(() => scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight), 60);
  }, []);

  useEffect(() => {
    loadList().then((list) => {
      const openId = searchParams.get("open");
      if (openId) openConvo(openId);
      else if (list.length && !active) openConvo(list[0].id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadList]);

  const send = async () => {
    if (!text.trim() || !active || sending) return;
    setSending(true);
    const content = text.trim();
    setText("");
    try {
      if (aiMode) {
        await api.post(`/conversations/${active.id}/simulate`, { content });
      } else {
        await api.post(`/conversations/${active.id}/messages`, { content, direction: "outbound", sender_type: "staff" });
      }
      await openConvo(active.id);
      loadList();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setSending(false); }
  };

  const setState = async (state) => {
    try {
      await api.put(`/conversations/${active.id}/state`, { state });
      toast.success(`Conversation set to ${convoStateMeta(state).label}`);
      await openConvo(active.id); loadList();
      if (state === "human_active") setAiMode(false);
      if (state === "ai_active") setAiMode(true);
    } catch { toast.error("Failed"); }
  };

  const createConvo = async () => {
    try {
      const { data } = await api.post("/conversations", { channel, contact_name: contactName || null, lead_id: leadId || null });
      toast.success("Conversation created");
      setNewOpen(false); setContactName(""); setLeadId("");
      await loadList(); openConvo(data.id);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const state = active ? convoStateMeta(ctx?.state || active.state) : null;

  return (
    <div className="animate-fade-up space-y-4" data-testid="conversations-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight text-slate-900">Conversations</h1>
          <p className="mt-1 text-sm text-slate-500">Unified inbox with Wellora AI · WhatsApp-ready</p>
        </div>
        <Button onClick={() => setNewOpen(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="new-conversation-button"><Plus className="h-4 w-4" /> New</Button>
      </div>

      <div className="grid gap-4 lg:grid-cols-[300px_1fr_300px]">
        {/* List */}
        <Card className="flex h-[72vh] flex-col overflow-hidden shadow-sm">
          <div className="border-b border-slate-100 p-3">
            <div className="relative"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="convo-search" /></div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {convos.length === 0 ? <p className="p-6 text-center text-sm text-slate-400">No conversations.</p>
              : convos.map((c) => (
                <button key={c.id} onClick={() => openConvo(c.id)} data-testid="convo-list-item"
                  className={cn("flex w-full flex-col items-start border-b border-slate-50 p-3 text-left transition-colors hover:bg-slate-50", active?.id === c.id && "bg-emerald-50/60")}>
                  <div className="flex w-full items-center justify-between">
                    <span className="font-medium text-slate-900">{c.contact_name}</span>
                    <Badge className={cn("text-[10px]", convoStateMeta(c.state).badge)}>{convoStateMeta(c.state).label}</Badge>
                  </div>
                  <span className="mt-0.5 line-clamp-1 text-xs text-slate-500">{c.channel} · {c.last_message_preview || "No messages"}</span>
                </button>
              ))}
          </div>
        </Card>

        {/* Thread */}
        <Card className="flex h-[72vh] flex-col overflow-hidden shadow-sm" data-testid="convo-thread">
          {!active ? (
            <div className="flex flex-1 flex-col items-center justify-center text-slate-400"><MessageSquare className="mb-2 h-8 w-8" />Select a conversation</div>
          ) : (
            <>
              <div className="flex items-center justify-between border-b border-slate-100 p-4">
                <div><p className="font-medium text-slate-900">{active.contact_name}</p><p className="text-xs text-slate-500">{active.channel}</p></div>
                <div className="flex items-center gap-2">
                  <Badge className={cn(state.badge)} data-testid="convo-state-badge">{state.label}</Badge>
                  {(ctx?.state === "human_required" || ctx?.state === "ai_active") && (
                    <Button size="sm" variant="outline" onClick={() => setState("human_active")} data-testid="take-over-button"><Headset className="h-4 w-4" /> Take over</Button>
                  )}
                  {ctx?.state === "human_active" && (
                    <Button size="sm" variant="outline" onClick={() => setState("ai_active")} data-testid="resume-ai-button"><Bot className="h-4 w-4" /> Resume AI</Button>
                  )}
                  {ctx?.state !== "resolved" && (
                    <Button size="sm" variant="ghost" onClick={() => setState("resolved")} data-testid="resolve-button">Resolve</Button>
                  )}
                </div>
              </div>

              <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto bg-slate-50/50 p-4">
                {messages.map((m) => {
                  const outbound = m.direction === "outbound";
                  return (
                    <div key={m.id} className={cn("flex", outbound ? "justify-end" : "justify-start")} data-testid="convo-message">
                      <div className={cn("max-w-[80%] rounded-lg px-3 py-2 text-sm", outbound ? (m.is_ai ? "bg-emerald-600 text-white" : "bg-slate-800 text-white") : "border border-slate-200 bg-white text-slate-800")}>
                        {outbound && (
                          <p className={cn("mb-0.5 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider", m.is_ai ? "text-emerald-100" : "text-slate-300")}>
                            {m.is_ai ? <><Sparkles className="h-3 w-3" /> Wellora AI</> : <><User className="h-3 w-3" /> Staff</>}
                          </p>
                        )}
                        <p className="whitespace-pre-wrap">{m.content}</p>
                        <p className={cn("mt-1 text-[10px]", outbound ? "text-white/60" : "text-slate-400")}>{fmtDateTime(m.created_at)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="border-t border-slate-100 p-3">
                <div className="mb-2 flex items-center gap-2">
                  <Switch checked={aiMode} onCheckedChange={setAiMode} disabled={ctx?.state === "human_active"} data-testid="ai-mode-toggle" />
                  <Label className="flex items-center gap-1 text-xs text-slate-500">
                    <Sparkles className="h-3.5 w-3.5 text-emerald-600" /> AI Test mode {aiMode ? "(simulate inbound patient message)" : "(send as staff)"}
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <Input placeholder={aiMode ? "Type a patient message to test the AI…" : "Type a staff reply…"} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} data-testid="convo-input" />
                  <Button onClick={send} disabled={sending} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="convo-send"><Send className="h-4 w-4" /></Button>
                </div>
              </div>
            </>
          )}
        </Card>

        {/* Context panel */}
        <Card className="hidden h-[72vh] flex-col overflow-y-auto p-4 shadow-sm lg:flex" data-testid="convo-context">
          {!ctx ? <p className="text-sm text-slate-400">Conversation context</p> : (
            <div className="space-y-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">CRM Context</p>
              {ctx.summary && <div className="rounded-md bg-slate-50 p-2 text-xs text-slate-500">{ctx.summary}</div>}
              {ctx.patient ? (
                <div className="rounded-lg border border-slate-100 p-3">
                  <p className="text-xs uppercase tracking-wider text-slate-400">Patient</p>
                  <p className="text-sm font-medium text-slate-900">{ctx.patient.full_name}</p>
                  <p className="font-mono text-xs text-slate-400">{ctx.patient.patient_code} · {ctx.patient.phone}</p>
                  <Button variant="outline" size="sm" className="mt-2 w-full" onClick={() => navigate(`/patients/${ctx.patient.id}`)} data-testid="ctx-view-patient"><ExternalLink className="h-3.5 w-3.5" /> Patient 360</Button>
                </div>
              ) : <p className="text-xs text-slate-400">No linked patient.</p>}
              {ctx.lead && (
                <div className="rounded-lg border border-slate-100 p-3">
                  <p className="text-xs uppercase tracking-wider text-slate-400">Lead</p>
                  <p className="text-sm font-medium text-slate-900">{ctx.lead.full_name}</p>
                  <div className="mt-1 flex flex-wrap gap-1">
                    <Badge variant="secondary" className="bg-slate-100 text-xs">{ctx.lead.source}</Badge>
                    <Badge variant="secondary" className="bg-slate-100 text-xs capitalize">{ctx.lead.status?.replace("_", " ")}</Badge>
                    <Badge variant="secondary" className="bg-slate-100 text-xs capitalize">{ctx.lead.priority}</Badge>
                  </div>
                  <Button variant="outline" size="sm" className="mt-2 w-full" onClick={() => navigate(`/leads/${ctx.lead.id}`)} data-testid="ctx-view-lead"><ExternalLink className="h-3.5 w-3.5" /> Open Lead</Button>
                </div>
              )}
              {ctx.upcoming_appointment && (
                <div className="rounded-lg border border-emerald-100 bg-emerald-50/40 p-3">
                  <p className="text-xs uppercase tracking-wider text-slate-400">Upcoming Appointment</p>
                  <p className="text-sm font-medium text-slate-900">{ctx.upcoming_appointment.service_name}</p>
                  <p className="text-xs text-slate-500">{ctx.upcoming_appointment.doctor_name} · {ctx.upcoming_appointment.date} {ctx.upcoming_appointment.start_time}</p>
                </div>
              )}
              <div><p className="text-xs uppercase tracking-wider text-slate-400">Assigned owner</p><p className="text-sm text-slate-700">{ctx.owner_name || "Unassigned"}</p></div>
            </div>
          )}
        </Card>
      </div>

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="sm:max-w-md" data-testid="new-convo-dialog">
          <DialogHeader><DialogTitle className="font-heading">New Conversation</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5"><Label>Channel</Label>
              <Select value={channel} onValueChange={setChannel}><SelectTrigger data-testid="convo-channel"><SelectValue /></SelectTrigger>
                <SelectContent>{CONVO_CHANNELS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-1.5"><Label>Link to lead (optional)</Label>
              <Select value={leadId || undefined} onValueChange={setLeadId}><SelectTrigger data-testid="convo-lead"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>{leads.map((l) => <SelectItem key={l.id} value={l.id}>{l.full_name} · {l.phone}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-1.5"><Label>Contact name</Label><Input value={contactName} onChange={(e) => setContactName(e.target.value)} placeholder="Optional if lead linked" data-testid="convo-contact" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewOpen(false)}>Cancel</Button>
            <Button onClick={createConvo} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="convo-create">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
